/**
 * Aircraft Detail Card — 지도에서 항공기를 클릭하면 뜨는 실시간 추적 카드.
 * 편명·노선(목적지)·고도·속도가 ADS-B 갱신 주기마다 자동으로 갱신된다.
 */
import { type FC, useEffect, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { sensorAccent } from '@/components/map/sensor-optics';
import { fetchAircraftRoute } from '@/services/aircraft-route';
import type { Aircraft, AircraftRoute } from '@/types';

const Row: FC<{ label: string; children: React.ReactNode; accent?: string }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 py-[3px]">
    <span className="font-mono text-[8px] tracking-[0.18em] text-slate-500 shrink-0">{label}</span>
    <span className="font-mono text-[11px] tabular-nums text-slate-200 text-right truncate">{children}</span>
  </div>
);

export const AircraftDetailCard: FC = () => {
  const selectedId  = useAppStore((s) => s.selectedAircraftId);
  const setSelected = useAppStore((s) => s.setSelectedAircraftId);
  const aircraft    = useAppStore((s) => s.aircraft);
  const militaryAir = useAppStore((s) => s.militaryAircraft);
  const sensorMode  = useAppStore((s) => s.sensorMode);
  const accent = sensorAccent(sensorMode);

  const [route, setRoute] = useState<AircraftRoute | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);

  // 스토어의 최신 배열에서 매번 다시 찾는다 → 위치·고도가 실시간 반영된다
  const ac: Aircraft | undefined =
    aircraft.find((a) => a.id === selectedId) ?? militaryAir.find((a) => a.id === selectedId);

  const callsign = ac?.callsign;

  useEffect(() => {
    if (!callsign) { setRoute(null); return; }
    let cancelled = false;
    setRouteLoading(true);
    fetchAircraftRoute(callsign)
      .then((r) => { if (!cancelled) setRoute(r); })
      .finally(() => { if (!cancelled) setRouteLoading(false); });
    return () => { cancelled = true; };
  }, [callsign]);

  // ESC 로 추적 해제
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, setSelected]);

  if (!selectedId) return null;

  // 선택은 했는데 최신 피드에 없다 = 커버리지 밖으로 나감
  const lost = !ac;
  const vs = ac?.verticalRateFpm ?? 0;

  return (
    <div className="absolute top-14 right-3 md:right-[43%] lg:right-[39%] md:mr-3 z-40 w-[248px] pointer-events-auto">
      <div
        className="glass-panel hud-corners px-3 py-2.5"
        style={{ boxShadow: `inset 0 0 0 1px ${accent}55, 0 0 24px ${accent}18` }}
      >
        {/* 헤더 */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[13px] font-semibold tracking-wider" style={{ color: accent }}>
                {ac?.callsign ?? selectedId.toUpperCase()}
              </span>
              {!lost && (
                <span className="w-1.5 h-1.5 rounded-full bg-threat-low animate-pulse" title="live" />
              )}
            </div>
            <div className="font-mono text-[9px] text-slate-500 truncate">
              {ac?.acDesc ?? ac?.acType ?? (lost ? '' : 'Unknown type')}
            </div>
          </div>
          <button
            onClick={() => setSelected(null)}
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-slate-700 transition-colors text-[11px]"
            title="Stop tracking (Esc)"
          >
            ✕
          </button>
        </div>

        {lost ? (
          <div className="py-3 text-center">
            <div className="font-mono text-[10px] tracking-[0.18em] text-threat-medium">SIGNAL LOST</div>
            <div className="font-mono text-[9px] text-slate-600 mt-1">out of ADS-B coverage</div>
          </div>
        ) : (
          <>
            {/* 노선 */}
            <div
              className="rounded-sm px-2 py-1.5 mb-1.5"
              style={{ background: `${accent}12`, boxShadow: `inset 0 0 0 1px ${accent}33` }}
            >
              {route ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 text-left">
                      <div className="font-mono text-[13px] font-semibold" style={{ color: accent }}>
                        {route.originIata ?? '···'}
                      </div>
                      <div className="font-mono text-[8px] text-slate-500 truncate">{route.originCity ?? ''}</div>
                    </div>
                    <span className="font-mono text-[11px] text-slate-600 shrink-0">→</span>
                    <div className="min-w-0 text-right">
                      <div className="font-mono text-[13px] font-semibold" style={{ color: accent }}>
                        {route.destIata ?? '···'}
                      </div>
                      <div className="font-mono text-[8px] text-slate-500 truncate">{route.destCity ?? ''}</div>
                    </div>
                  </div>
                  {route.airline && (
                    <div className="font-mono text-[8px] text-slate-600 mt-1 truncate">{route.airline}</div>
                  )}
                </>
              ) : (
                <div className="font-mono text-[9px] text-slate-600 text-center py-1">
                  {routeLoading ? 'RESOLVING ROUTE…' : 'ROUTE NOT IN DATABASE'}
                </div>
              )}
            </div>

            {/* 실시간 텔레메트리 */}
            <Row label="ALT">
              {Math.round(ac.altitudeFt).toLocaleString()} ft
              <span className="text-slate-500"> · {Math.round(ac.altitudeFt * 0.3048).toLocaleString()} m</span>
            </Row>
            <Row label="SPD">
              {Math.round(ac.speedKt).toLocaleString()} kt
              <span className="text-slate-500"> · {Math.round(ac.speedKt * 1.852).toLocaleString()} km/h</span>
            </Row>
            <Row label="HDG">{String(Math.round(ac.heading)).padStart(3, '0')}°</Row>
            {vs !== 0 && (
              <Row label="V/S">
                <span style={{ color: vs > 0 ? '#8ff7c7' : '#ffb72d' }}>
                  {vs > 0 ? '▲' : '▼'} {Math.abs(Math.round(vs)).toLocaleString()} fpm
                </span>
              </Row>
            )}
            <Row label="POS">{ac.lat.toFixed(3)}, {ac.lon.toFixed(3)}</Row>
            {ac.registration && <Row label="REG">{ac.registration}</Row>}
            {ac.operator && <Row label="OP">{ac.operator}</Row>}
            {ac.squawk && <Row label="SQK">{ac.squawk}</Row>}
            {ac.country && <Row label="CTY">{ac.country}</Row>}
          </>
        )}
      </div>
    </div>
  );
};
